import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useForm } from "../../hooks/useForm";

import IconButton from "@material-ui/core/IconButton";
import { SubForm } from "./styles";
import CloseIcon from "@material-ui/icons/Close";
import {
  Box,
  Input,
  Button,
  FormHelperText,
  NativeSelect,
  Grid,
  ButtonGroup,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import { currencySelector } from "../../redux/currencyReducer";
import Preloader from "../Common/Preloader";
import { paymentSelector, paymentFormIsError, paymentFormSucces } from "../../redux/paymentReducer";
import { IPaymentData, ICurrency } from "../../interfaces";
import { Alert } from "@material-ui/lab";
import { useTranslation } from "react-i18next";

const antiDoubleSystem = {
  halls: JSON.parse(process.env.ANTI_DOUBLE_HALLS!) as number[],
  timeout: (+process.env.ANTI_DOUBLE_TIMEOUT! * 1000) as number,
  lastActions: [] as number[],
};

interface PaymentFormProps {
  id: any;
  hidePayment: () => void;
  balance: any;
  currency_id?: string;
  take: (data: IPaymentData, id?: any, info?: boolean) => void;
  putEvent: (data: IPaymentData, id?: any, info?: boolean) => void;
  info?: boolean;
  ownerBalance?: number | undefined;
  hallLanguage?: string;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ id, hidePayment, currency_id, take, putEvent, info, balance, ownerBalance, hallLanguage }) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();

  const {
    form: { error, loading, payment },
  } = useSelector(paymentSelector);

  const { items: currencyList } = useSelector(currencySelector);

  const [currency, setCurrency] = useState(currency_id ? currency_id : "");
  const [paymentMessage, setPaymentMessage] = useState("");
  const [viaPaymentSystem, setViaPaymentSystem] = useState(false); // Новое состояние для галочки

  const { form, handleInputChange, setFormItem } = useForm({
    balance: 0,
  });

  // Добавили via_payment_system в отправляемые данные
  const data: IPaymentData = useMemo(() => ({
    currency_id: currency,
    amount: form.balance * 100,
    via_payment_system: viaPaymentSystem,
  }), [currency, form.balance, viaPaymentSystem]);

  useEffect(() => {
    return () => {
      dispatch(paymentFormIsError(null));
      dispatch(paymentFormSucces(null));
    };
  }, [dispatch]);

  const subBalance = useCallback((sum: number) => {
    dispatch(paymentFormIsError(null));
    dispatch(paymentFormSucces(null));

    if (id) {
      take({
        currency_id: currency,
        amount: sum,
        via_payment_system: viaPaymentSystem,
      }, id, info);
    } else {
      take(data);
    }

    setFormItem("balance", 0);
    setPaymentMessage(`Баланс был cнят на ${sum / 100}(${currency})`);
  }, [dispatch, id, currency, take, info, data, setFormItem, viaPaymentSystem]);

  const addBalance = useCallback((sum: number) => {
    let extra = "";
    if (id && antiDoubleSystem.halls.includes(id.hall_id)) {
      const time = Date.now();
      if (sum in antiDoubleSystem.lastActions && antiDoubleSystem.lastActions[sum] + antiDoubleSystem.timeout > time) {
        const x = Math.floor(Math.random() * 5) + 1;
        const y = Math.floor(Math.random() * 5) + 1;
        const z = +(window.prompt(`Введите сумму чисел ${x} + ${y}`) ?? "").trim();
        if (x + y !== z) {
          alert("Неверный ответ, попробуйте ещё раз");
          return;
        }
        extra = `${x}+${y}=${z}`;
      } else {
        antiDoubleSystem.lastActions[sum] = time;
      }
    }

    dispatch(paymentFormIsError(null));
    dispatch(paymentFormSucces(null));

    if (id) {
      putEvent({
        currency_id: currency,
        amount: sum,
        extra,
        via_payment_system: viaPaymentSystem,
      }, id, info);
    } else {
      putEvent(data);
    }

    setFormItem("balance", 0);
    setPaymentMessage(`Баланс был пополнен на ${sum / 100}(${currency})`);
  }, [dispatch, id, currency, putEvent, info, data, setFormItem, viaPaymentSystem]);

  const currentBalance = useMemo(() => {
    return !currency_id ? balance[currency] : balance;
  }, [currency_id, balance, currency]);

  const fastActions = useMemo(() => {
    if (id?.login && hallLanguage === "TGK") {
      return [5, 10, 20, 50, 100, 200, 500, 1000];
    }
    return [50, 100, 500, 1000, 2000, 3000, 4000, 5000];
  }, [id?.login, hallLanguage]);

  const handleCurrencySelect = useCallback((e: React.ChangeEvent<{ value: unknown }>) => {
    setCurrency(e.target.value as string);
  }, []);

  const handleViaPaymentSystemChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setViaPaymentSystem(e.target.checked);
  }, []);

  const handleWithdrawCurrentBalance = useCallback(() => {
    subBalance(currentBalance);
  }, [subBalance, currentBalance]);

  const handleFormWithdraw = useCallback(() => {
    dispatch(paymentFormIsError(null));
    dispatch(paymentFormSucces(null));
    id ? take(data, id, info) : take(data);
    setFormItem("balance", 0);
    setPaymentMessage(`Баланс был cнят на ${form.balance}(${currency})`);
  }, [dispatch, id, take, data, info, setFormItem, form.balance, currency]);

  const handleFormAdd = useCallback(() => {
    addBalance(100 * form.balance);
  }, [addBalance, form.balance]);

  return (
    <SubForm>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <h3>Изменение баланса <b>{id?.name}</b></h3>
        <IconButton onClick={hidePayment}>
          <CloseIcon />
        </IconButton>
      </Box>

      {!!payment && (
        <Alert severity="success" style={{ marginBottom: "20px" }}>
          {paymentMessage}
        </Alert>
      )}

      {!!error.length && (
        <Alert severity="error" style={{ marginBottom: "20px" }}>
          {error[0].message}
        </Alert>
      )}

      {loading ? (
        <Preloader absolute={false} />
      ) : (
        <Grid container alignItems="center">
          {id?.login ? (
            <Grid item xs={12}>
              Быстрые действия:{" "}
                <ButtonGroup variant="contained" style={{ marginBottom: "15px", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "start", boxShadow: "none" }}>
                {fastActions.map((sum) => (
                  <Button
                    key={sum}
                    style={{ backgroundColor: "green", boxShadow: "0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)" }}
                    color="primary"
                    size="small"
                    disabled={!currency || (sum > (ownerBalance || 0) / 100)}
                    onClick={() => addBalance(100 * sum)}
                  >
                    +{sum}
                  </Button>
                ))}
                <Button
                  style={{ backgroundColor: "red", boxShadow: "0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)" }}
                  color="primary"
                  size="small"
                  disabled={!currency || currentBalance <= 0}
                  onClick={handleWithdrawCurrentBalance}
                >
                  Вывести {(currentBalance / 100).toFixed(2)}
                </Button>
              </ButtonGroup>
            </Grid>
          ) : (
            ""
          )}

          <Grid item xs={9}>
            <Input
              value={form.balance ? form.balance : 0}
              name="balance"
              onChange={handleInputChange}
              placeholder="Сначала выберите валюту"
              disabled={!currency}
            />
          </Grid>

          <Grid item xs={3}>
            <NativeSelect
              onChange={handleCurrencySelect}
              value={currency}
              disabled={!!currency_id}
            >
              <option aria-label="None" value="" />
              {!!currencyList.length && currencyList.map((item: ICurrency) => (
                <option value={item.id} key={item.id}>
                  {item.id}
                </option>
              ))}
            </NativeSelect>
            <FormHelperText>{t("ru.halls.form_helpers.currency")}</FormHelperText>
          </Grid>

          {/* Новая галочка для проведения через платёжную систему */}
          {(id?.login ? null : true) ?? <Grid item xs={12} style={{ marginTop: "10px", marginBottom: "10px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={viaPaymentSystem}
                  onChange={handleViaPaymentSystemChange}
                  color="primary"
                />
              }
              label="Провести через платёжную систему"
            />
          </Grid>}

          <Grid item xs={12}>
            <ButtonGroup variant="contained">
              <Button
                variant="outlined"
                color="secondary"
                disabled={!currency || !!(form.balance > currentBalance / 100)}
                onClick={handleFormWithdraw}
              >
                {t("ru.common.balance.withdraw")}
              </Button>

              <Button
                variant="outlined"
                color="primary"
                disabled={!currency || (!!ownerBalance && !!(form.balance > ownerBalance / 100))}
                onClick={handleFormAdd}
              >
                {t("ru.common.balance.change")}
              </Button>
            </ButtonGroup>
          </Grid>
        </Grid>
      )}
    </SubForm>
  );
};

export default PaymentForm;